// ===============================================================
// shellSessionGeneration.js — one pure helper dedicated to issuing
// strictly-increasing shell-session generation values.
// ===============================================================
//
// See NativeBottomNavPlugin.beginShellSession's own doc comment for why
// this exists: native fences session establishment against a delayed,
// obsolete begin request using this value, comparing it against whatever
// generation is currently active. That only works if two DIFFERENT
// sessions created within the same JS global/context can never receive
// the same (or a decreasing) generation - raw Date.now() alone cannot
// guarantee this, since two sessions (e.g. a React StrictMode
// mount/cleanup/remount, or two rapid uid transitions) can easily be
// issued within the same millisecond and would otherwise collide.
//
// issueShellSessionGeneration() therefore combines a monotonic clock
// reading with a module-level high-water mark:
//
//   next = max(monotonicCandidate, lastIssuedGeneration + 1)
//
// - monotonicCandidate prefers performance.timeOrigin + performance.now()
//   (a monotonic clock per the Performance API spec - unaffected by
//   system wall-clock adjustments once a page/context has started),
//   floored to an integer millisecond value. Date.now() is only used as
//   a fallback when the Performance API is unavailable.
// - lastIssuedGeneration + 1 is the real guarantee: since it strictly
//   increases by at least 1 on every call regardless of what the clock
//   reports, two calls in the same millisecond, or even a backward wall-
//   clock jump, can never produce a repeated or decreasing value.
//
// The result is always clamped to stay a finite, non-negative,
// Number.isSafeInteger()-safe integer, which is also always representable
// exactly as a Java long (Number.MAX_SAFE_INTEGER, ~9.007e15, is far
// below Long.MAX_VALUE, ~9.223e18) - so the exact same value native
// receives and echoes back can be compared and stored losslessly on
// both sides.
//
// Never derived from uid/email/token/user data - purely a same-process,
// same-context ordering token.
//
// IMPORTANT SCOPE NOTE: lastIssuedGeneration is module-level state, so
// issueShellSessionGeneration() is monotonic only WITHIN ONE JavaScript
// global/context - it is NOT globally monotonic across a true WebView/
// new-JS-context recreation, since that destroys this module along with
// everything else in the old context and a fresh copy starts back at 0.
// NativeBottomNavPlugin, by contrast, is native/Android-process-lifetime
// state - it can still be holding a much larger currentSessionGeneration
// left over from a PRIOR JS context when a brand-new context's first
// begin attempt proposes a low value and gets correctly rejected. This is
// not a defect in issueShellSessionGeneration() itself - recovery across
// that boundary is handled one level up, by NativeShellController.js
// rebasing this module's own high-water mark past whatever generation
// native reports (see advanceShellSessionGenerationPast below) and
// retrying once - never by trying to make this module itself aware of
// native's state.

let lastIssuedGeneration = 0;

function monotonicNowMs() {
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function" &&
    typeof performance.timeOrigin === "number" &&
    Number.isFinite(performance.timeOrigin)
  ) {
    const candidate = performance.timeOrigin + performance.now();
    if (Number.isFinite(candidate) && candidate >= 0) {
      return candidate;
    }
  }
  // Fallback when the Performance API is unavailable - still passed
  // through the same module-level high-water guard below, so same-
  // millisecond collisions and any backward jump are still safe.
  return Date.now();
}

export function issueShellSessionGeneration() {
  const floorValue = lastIssuedGeneration + 1;
  const candidate = Math.floor(monotonicNowMs());

  let next = Number.isFinite(candidate) && candidate > floorValue ? candidate : floorValue;

  if (!Number.isFinite(next) || next < 0) {
    next = floorValue;
  }
  if (next > Number.MAX_SAFE_INTEGER) {
    next = Number.MAX_SAFE_INTEGER;
  }
  // The clamp above could in principle pull next back below the required
  // floor (only possible if lastIssuedGeneration itself were already at
  // MAX_SAFE_INTEGER) - never regress in that case either; there is
  // simply no larger safe integer left to issue, so the value saturates.
  if (next < floorValue) {
    next = Number.MAX_SAFE_INTEGER;
  }

  lastIssuedGeneration = next;
  return next;
}

/**
 * Recovers this JS context's generator across a WebView/new-JS-context
 * recreation - see this module's own "IMPORTANT SCOPE NOTE" above for why
 * this is needed at all. Called by NativeShellController.js only after a
 * beginShellSession request was rejected and native reported its own
 * currentSessionGeneration in the response (see
 * NativeBottomNavPlugin.beginShellSession) - raises this module's
 * high-water mark to at least that value, so the VERY NEXT
 * issueShellSessionGeneration() call is guaranteed to return a value
 * strictly greater than it, letting a single retry win the race
 * regardless of how far behind this context's own clock/high-water mark
 * had fallen (a genuinely new context always starts low; a device
 * wall-clock rollback can also leave a "new" context's monotonic clock
 * reading behind an old, still-native-retained generation).
 *
 * A malformed input (not a finite, non-negative, Number.isSafeInteger()
 * value) is ignored entirely and safely - the high-water mark is left
 * exactly as it was, never lowered, never corrupted by an untrusted
 * value. Only ever raises the mark, never lowers it - a nativeGeneration
 * at or below the current mark is simply a no-op.
 */
export function advanceShellSessionGenerationPast(nativeGeneration) {
  if (
    typeof nativeGeneration !== "number" ||
    !Number.isFinite(nativeGeneration) ||
    !Number.isSafeInteger(nativeGeneration) ||
    nativeGeneration < 0
  ) {
    return;
  }
  if (nativeGeneration > lastIssuedGeneration) {
    lastIssuedGeneration = nativeGeneration;
  }
}
